import React from 'react';
import { Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { translations } from '../translations';

const PricingTable = ({ language = 'en' }) => {
  const t = translations[language].pricing;

  const tiers = [
    {
      name: t.plans.basic.name,
      price: t.plans.basic.price,
      period: t.plans.basic.period,
      description: t.plans.basic.description,
      features: [
        { name: t.plans.basic.features[0], included: true },
        { name: t.plans.basic.features[1], included: true },
        { name: t.plans.basic.features[2], included: true },
        { name: t.plans.basic.features[3], included: true },
        { name: t.plans.basic.features[4], included: false },
        { name: t.plans.basic.features[5], included: false },
        { name: t.plans.basic.features[6], included: false },
        { name: t.plans.basic.features[7], included: false }
      ],
      cta: t.plans.basic.cta,
      popular: false,
    },
    {
      name: t.plans.professional.name,
      price: t.plans.professional.price,
      period: t.plans.professional.period,
      description: t.plans.professional.description,
      features: [
        { name: t.plans.professional.features[0], included: true },
        { name: t.plans.professional.features[1], included: true },
        { name: t.plans.professional.features[2], included: true },
        { name: t.plans.professional.features[3], included: true },
        { name: t.plans.professional.features[4], included: true },
        { name: t.plans.professional.features[5], included: true },
        { name: t.plans.professional.features[6], included: false },
        { name: t.plans.professional.features[7], included: false }
      ],
      cta: t.plans.professional.cta,
      popular: true,
    },
    {
      name: t.plans.enterprise.name,
      price: t.plans.enterprise.price,
      period: t.plans.enterprise.period,
      description: t.plans.enterprise.description,
      features: [
        { name: t.plans.enterprise.features[0], included: true },
        { name: t.plans.enterprise.features[1], included: true },
        { name: t.plans.enterprise.features[2], included: true },
        { name: t.plans.enterprise.features[3], included: true },
        { name: t.plans.enterprise.features[4], included: true },
        { name: t.plans.enterprise.features[5], included: true },
        { name: t.plans.enterprise.features[6], included: true },
        { name: t.plans.enterprise.features[7], included: true }
      ],
      cta: t.plans.enterprise.cta,
      popular: false,
    },
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2c3e50] mb-3 sm:mb-4">
            {t.title}
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600">
            {t.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`relative rounded-2xl shadow-sm bg-white border-2 hover:shadow-xl transition-shadow duration-300 ${
                tier.popular
                  ? 'border-[#3498DB] shadow-[#3498DB]/10 lg:scale-105 lg:-translate-y-2'
                  : 'border-gray-100'
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-4 sm:-top-5 left-0 right-0 mx-auto w-32 sm:w-36 rounded-full bg-gradient-to-r from-[#3498DB] to-[#2980B9] text-white text-xs sm:text-sm font-semibold py-1.5 sm:py-2 px-3 sm:px-4 text-center transform">
                  {t.plans.professional.popular}
                </div>
              )}

              <div className="p-6 sm:p-8">
                <h3 className="text-lg sm:text-xl font-semibold text-[#2c3e50] mb-2">
                  {tier.name}
                </h3>
                <div className="flex items-baseline flex-wrap gap-1 mb-2">
                  <span className="text-3xl sm:text-4xl font-bold text-[#2c3e50]">
                    {tier.price}
                  </span>
                  <span className="text-sm sm:text-base text-gray-500">{tier.period}</span>
                </div>
                <p className="text-sm sm:text-base text-gray-500 mb-6 min-h-[2.5rem]">{tier.description}</p>

                <Link
                  to="/coming-soon"
                  className={`w-full rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 text-sm font-semibold transition-all duration-300 flex items-center justify-center ${
                    tier.popular
                      ? 'bg-[#3498DB] text-white hover:bg-[#2980B9] shadow-lg hover:shadow-xl hover:shadow-[#3498DB]/20'
                      : 'bg-[#2c3e50] text-white hover:bg-[#2c3e50]/90 hover:shadow-lg'
                  }`}
                >
                  {tier.cta}
                </Link>

                <div className="mt-6 sm:mt-8 space-y-3 sm:space-y-4">
                  {tier.features.map((feature) => (
                    <div
                      key={feature.name}
                      className="flex items-start sm:items-center space-x-3"
                    >
                      {feature.included ? (
                        <Check className="w-5 h-5 text-[#3498DB] flex-shrink-0 mt-0.5 sm:mt-0" />
                      ) : (
                        <X className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5 sm:mt-0" />
                      )}
                      <span
                        className={`text-sm sm:text-base ${
                          feature.included ? 'text-gray-600' : 'text-gray-400'
                        }`}
                      >
                        {feature.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingTable; 